import React, { useEffect, useState } from "react";
import Header from "src/components/app/layout/Header";
import Layout from "src/components/app/layout/Layout";
import { useNavigate, useParams } from "react-router-dom";
import Input from "src/components/app/Input";
import Radio from "src/components/common/Radio";
import Button from "src/components/app/Button";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { updateProfileRequest } from "src/redux/slices/authSlice";
import { NUMERICS } from "src/constants/regex";
import { toast } from "sonner";
import { getPatientDetailsRequest } from "src/redux/slices/authSlice";
import analytics from "src/helpers/analytics";
import { SCREEN_NAMES } from "src/helpers/analytics/events";
const AddCareGiver: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { patient, updateProfile } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState<{
    name: string;
    phone: string;
    relation: string;
  }>({
    name: "",
    phone: "",
    relation: "",
  });

  useEffect(() => {
    // page view
    analytics.pageView(SCREEN_NAMES.ADD_CAREGIVER_SCREEN);
  }, []);

  // Pre-populate form if caregiver exists
  useEffect(() => {
    if (patient?.data?.patient_meta_info) {
      const metaInfo = patient.data.patient_meta_info;
      setFormData({
        name: metaInfo.cg_name || "",
        phone: metaInfo.cg_contact_no || "",
        relation: metaInfo.cg_relation || "",
      });
    }
  }, [patient]);

  const [errors, setErrors] = useState<{
    name: string;
    phone: string;
    relation: string;
  }>({
    name: "",
    phone: "",
    relation: "",
  });

  const patientRelations = [
    { label: "Mother", value: "mother" },
    { label: "Father", value: "father" },
    { label: "Brother", value: "brother" },
    { label: "Sister", value: "sister" },
    { label: "Friend", value: "friend" },
    { label: "Relative", value: "relative" },
    { label: "Other", value: "other" },
  ];

  const validateField = (fieldName: string, value: string) => {
    let error = "";

    switch (fieldName) {
      case "name":
        if (!value.trim()) {
          error = "Caregiver name is required";
        } else if (value.length < 3) {
          error = "Name must be at least 3 characters";
        }
        break;

      case "phone":
        if (!value.trim()) {
          error = "Phone number is required";
        } else if (!NUMERICS.test(value)) {
          error = "Phone number must contain only digits";
        } else if (value.length !== 10) {
          error = "Phone number must be 10 digits";
        }
        break;

      case "relation":
        if (!value) {
          error = "Relation is required";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name: fieldName, value: fieldValue } = e.target;

    setFormData((prev) => ({ ...prev, [fieldName]: fieldValue }));

    const error = validateField(fieldName, fieldValue);
    setErrors((prev) => ({ ...prev, [fieldName]: error }));
  };

  const handleAddCaregiver = () => {
    // Validate all required fields
    const newErrors = { ...errors };
    let hasErrors = false;

    const requiredFields = ["name", "phone", "relation"];

    requiredFields.forEach((key) => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) {
        newErrors[key as keyof typeof errors] = error;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      toast.error("Please fill all required fields correctly");
      return;
    }

    const payload = {
      patient_meta_info: {
        ...patient?.data?.patient_meta_info,
        cg_name: formData.name,
        cg_relation: formData.relation,
        cg_contact_no: formData.phone,
      },
    };

    new Promise((resolve, reject) => {
      dispatch(
        updateProfileRequest({
          payload: {
            payload,
            header: {
              userFrom: "PSP",
              source: "GoodFlipCare",
            },
          },
          resolve,
          reject,
        })
      );
    })
      .then((res: any) => {
        if (Number(res?.code || 0) === 1) {
          const isEdit = !!patient?.data?.patient_meta_info?.cg_name;
          toast.success(
            isEdit
              ? "Caregiver updated successfully"
              : "Caregiver added successfully"
          );
          new Promise((resolve, reject) => {
            dispatch(
              getPatientDetailsRequest({
                resolve,
                reject,
              })
            );
          })
            .then((res: any) => {
              if (Number(res?.code || 0) === 1) {
                const supportProgramKey =
                  res?.data?.patient_meta_info?.support_program_key;
                navigate(`/app/${supportProgramKey}/profile-screen`, {
                  replace: true,
                });
              }
            })
            .catch((err: any) => {
              console.error("err", err);
            });
        } else {
          toast.error(res?.msg || "Failed to save caregiver details");
        }
      })
      .catch((error) => {
        console.error("Error saving caregiver:", error);
        toast.error("Failed to save caregiver details");
      });
  };

  const isEditMode = !!patient?.data?.patient_meta_info?.cg_name;

  return (
    <Layout
      supportProgramKey={supportProgramKey || ""}
      header={
        <Header
          title={isEditMode ? "Edit Caregiver" : "Add Caregiver"}
          titlePosition="center"
          showBackButton={true}
          onBackClick={() => {
            navigate(-1);
          }}
        />
      }
      stickyHeader={true}
      footer={
        <div className="bg-white p-4 border-t border-gray-200">
          <Button
            rounded="full"
            fullWidth={true}
            onClick={handleAddCaregiver}
            loading={updateProfile?.isLoading}
            disabled={updateProfile?.isLoading}
          >
            {isEditMode ? "Update Caregiver" : "Add Caregiver"}
          </Button>
        </div>
      }
      stickyFooter={true}
    >
      <div className="relative w-full px-4 py-4">
        <div className="flex flex-col gap-y-3">
          <Input
            label="Caregiver Name"
            name="name"
            type="text"
            placeholder="Enter caregiver name"
            fullWidth={true}
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />

          <Input
            label="Caregiver Phone Number"
            name="phone"
            type="tel"
            placeholder="Enter caregiver phone number"
            fullWidth={true}
            startAdornment={<p className="text-black text-sm pl-1">+91</p>}
            value={formData.phone}
            onChange={handleChange}
            error={!!errors.phone}
            helperText={errors.phone}
            maxLength={10}
          />

          <div className="relative w-full">
            <label
              htmlFor="relation"
              className="block text-sm font-medium text-[var(--text-secondary)] mb-2"
            >
              Relation with the patient <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-col gap-y-2">
              {patientRelations.map((relation, index) => (
                <div
                  className="flex items-center gap-2"
                  key={index}
                  onClick={() => {
                    setFormData({ ...formData, relation: relation.value });
                    setErrors((prev) => ({ ...prev, relation: "" }));
                  }}
                >
                  <Radio checked={formData.relation === relation.value} />
                  <span className="font-medium text-sm text-gray-500">
                    {relation.label}
                  </span>
                </div>
              ))}
            </div>
            {errors.relation && (
              <p className="text-xs text-red-500 mt-1">{errors.relation}</p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddCareGiver;
